import Dropdown from '@asphalt-react/dropdown';
import React, { useContext, useEffect, useState } from 'react';
import LoadingPage from './LoadingPage';
import Permission from '../../authorization/components/Permission';
import actions from '../../authorization/models/actions';
import roles from '../../authentication/models/roles';
import { UserContext } from '../hooks/UserContext';
import { getAllUsers, updateUserRole } from '../../authentication/api/userApi';
import styles from './AdminsPage.module.css';

const roleItems = [
    { id: '0', key: 'Viewer',    identifier: roles.VIEWER.name },
    { id: '1', key: 'Owner',     identifier: roles.OWNER.name },
    { id: '2', key: 'Developer', identifier: roles.DEVELOPER.name },
    { id: '3', key: 'Admin',     identifier: roles.ADMIN.name },
];

function UserRow({ user, isSelf }) {
    const [savingRole, setSavingRole] = useState(false);
    const [roleError, setRoleError] = useState(null);

    const currentRoleItem = roleItems.find((item) => item.identifier === user.role) ?? roleItems[0];

    const handleRoleChange = async (selectedItem) => {
        if (selectedItem.identifier === user.role) return;
        setSavingRole(true);
        setRoleError(null);
        try {
            await updateUserRole(user.uid, selectedItem.identifier);
        } catch (error) {
            console.error(error);
            setRoleError('Failed to update role.');
        } finally {
            setSavingRole(false);
        }
    };

    return (
        <div className={styles.userItemContainer}>
            <div className={styles.userInfo}>
                <p className={styles.userName}>{user.name}{isSelf && <span className={styles.selfBadge}> (you)</span>}</p>
                <p className={styles.userEmail}>{user.email}</p>
            </div>
            <div className={styles.roleDropdownContainer}>
                <Dropdown
                    items={roleItems}
                    initialSelectedItem={currentRoleItem}
                    onChange={handleRoleChange}
                    disabled={isSelf || savingRole}
                />
                {roleError && <p className={styles.rowError}>{roleError}</p>}
            </div>
        </div>
    );
}

function AdminsPage() {
    const { user: currentUser } = useContext(UserContext);
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        getAllUsers()
            .then((allUsers) => {
                setUsers(allUsers);
                setIsLoading(false);
            })
            .catch((error) => {
                console.error(error);
                setIsLoading(false);
            });
    }, []);

    return (
        <React.Fragment>
            {isLoading ? (
                <LoadingPage />
            ) : (
                <Permission
                    action={actions.ASSIGN_DEVELOPER_PERMISSION}
                    alternate={<p className={styles.unauthorizedText}>You do not have permission to view this page.</p>}
                >
                    <div className={styles.adminsPageContainer}>
                        <div className={styles.headingAndButtonContainer}>
                            <h3 className={styles.heading}>User Permissions</h3>
                        </div>
                        <div className={styles.columnHeaders}>
                            <span className={styles.columnHeaderUser}>User</span>
                            <span className={styles.columnHeaderRole}>Role</span>
                        </div>
                        <div className={styles.userListContainer}>
                            {users.map((user) => (
                                <UserRow
                                    key={user.uid}
                                    user={user}
                                    isSelf={user.uid === currentUser.uid}
                                />
                            ))}
                        </div>
                    </div>
                </Permission>
            )}
        </React.Fragment>
    );
}

export default AdminsPage;
